<template>
  <el-config-provider :locale="elementPlusLocale">
    <div class="user-center-page-entry">
      <component
        :is="currentComponent"
        v-if="currentComponent && isStandalone"
        :key="renderedRouteKey"
      />
      <UserCenterShell v-else-if="currentComponent">
        <component :is="currentComponent" :key="renderedRouteKey" class="qiankun-page" />
      </UserCenterShell>
      <div v-else class="user-center-page-loading">
        <el-icon class="user-center-page-loading-icon is-loading" :size="40">
          <Loading />
        </el-icon>
        <p class="user-center-page-loading-text">loading...</p>
      </div>
      <NewCustomerTaskRewardPopup />
    </div>
  </el-config-provider>
</template>

<script setup lang="ts">
import { computed, markRaw, ref, shallowRef, watch } from "vue";
import { useRoute } from "#imports";
import store from "../src/store";
import { getPageTitle } from "../src/utils/tools";
import { ensureUserCenterBootstrapped } from "./bootstrap.client";
import { getUserCenterViewLoader } from "./manifest";
import UserCenterShell from "./UserCenterShell.vue";
import NewCustomerTaskRewardPopup from "../src/components/NewCustomerTaskRewardPopup.vue";
import { Loading } from "@element-plus/icons-vue";
import userCenterI18n from "../src/utils/i18n";
import { getElementAndDayjsLocaleByI18nLocale } from "../src/utils/elementLocale";

definePageMeta({
  layout: false,
  pageTransition: false,
});

const route = useRoute();
const currentComponent = shallowRef();
const renderedRouteKey = ref(String(route.fullPath || ""));
let loadSequence = 0;

const isStandalone = computed(() => Boolean(route.meta.userCenterStandalone));
const elementPlusLocale = computed(() =>
  getElementAndDayjsLocaleByI18nLocale(String(userCenterI18n.global.locale.value || "zh-TW")).elementLocale,
);

watch(
  () => route.fullPath,
  async () => {
    const sequence = ++loadSequence;
    const nextFullPath = String(route.fullPath || "");

    if (process.server) {
      currentComponent.value = null;
      return;
    }

    await ensureUserCenterBootstrapped();
    if (sequence !== loadSequence) {
      return;
    }

    const viewKey = String(route.meta.userCenterViewKey || "");
    const loader = getUserCenterViewLoader(viewKey);
    if (!loader) {
      if (sequence === loadSequence) {
        currentComponent.value = null;
        renderedRouteKey.value = nextFullPath;
      }
      return;
    }

    const module = await loader();
    if (sequence !== loadSequence || String(route.fullPath || "") !== nextFullPath) {
      return;
    }

    currentComponent.value = markRaw(module.default || module);
    renderedRouteKey.value = nextFullPath;

    await store.dispatch("app/setRoute", route as any);
    if (route.meta.getFundInfo) {
      await store.dispatch("user/getFundInfo");
    }

    if (process.client) {
      const pageTitle = String(route.meta.pageTitle || route.meta.title || "");
      const storeInfoRaw = window.localStorage.getItem("StoreInformation");
      let localTitle = "";
      if (storeInfoRaw) {
        try {
          localTitle = JSON.parse(storeInfoRaw)?.title || "";
        }
        catch (error) {
          console.error(error);
        }
      }
      document.title = getPageTitle(pageTitle, localTitle);
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.user-center-page-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: min(520px, 72vh);
  padding: 48px 24px;
  box-sizing: border-box;
  text-align: center;
  color: #606266;
}

.user-center-page-loading-icon {
  color: var(--el-color-primary, #409eff);
}

.user-center-page-loading-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}
.user-center-page-entry{
  background-color: #F7F8FA;
}
</style>
